import { useEffect, useState } from 'react';
import axios from 'axios';
import { Mail, Save, Send, ShieldCheck, Lock, Server, Radar, Sparkles, ChevronDown, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// Riquadro richiudibile (26/10/2026): chiuso mostra solo titolo e stato, così tutte le sezioni
// stanno in una pagina; il clic sull'intestazione lo apre.
function SettingsCard({ icon, title, status, onSubmit, children }: {
  icon?: React.ReactNode;
  title: string;
  status?: { label: string; className: string };
  onSubmit?: (e: React.FormEvent) => void;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const header = (
    <button type="button" onClick={() => setOpen(v => !v)} className={`w-full flex items-center gap-2 text-left ${open ? 'mb-1' : ''}`}>
      {open ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
      {icon}
      <h2 className="font-medium text-gray-900">{title}</h2>
      {status && <span className={`badge ml-auto ${status.className}`}>{status.label}</span>}
    </button>
  );
  const body = open && <div className="mt-2">{children}</div>;
  return onSubmit
    ? <form onSubmit={onSubmit} className="card p-5 mb-3">{header}{body}</form>
    : <div className="card p-5 mb-3">{header}{body}</div>;
}

const CONFIGURED = { label: 'Configured', className: 'bg-green-100 text-green-700' };
const NOT_CONFIGURED = { label: 'Not configured', className: 'bg-gray-100 text-gray-500' };

interface SmtpSettings {
  smtp_host: string;
  smtp_port: string;
  smtp_secure: string;
  smtp_user: string;
  smtp_from: string;
  smtp_pass_set: boolean;
  gateway_host: string;
  gateway_port: string;
  // IP/CIDR autorizzati al push Athena XDR sulla porta dedicata (ingestion/athenaPush.ts).
  athena_push_allowed_ips: string;
  virustotal_api_key_set: boolean;
  abuseipdb_api_key_set: boolean;
  // Chiave NVD facoltativa per il controllo delle vulnerabilità del software installato.
  nvd_api_key_set: boolean;
  // Notifiche per alert (jobs/alertDispatch.ts): link al portale nelle email, webhook voce.
  portal_url: string;
  voice_webhook_url: string;
  voice_webhook_token_set: boolean;
  // Pagina "AI security considerations" dei report (services/reportAi.ts).
  anthropic_api_key_set: boolean;
  ai_model: string;
}

// Impostazioni globali della piattaforma (valide per tutti i partner e tenant).
export default function SettingsPage() {
  const { user, isAdmin } = useAuth();
  const [settings, setSettings] = useState<SmtpSettings | null>(null);
  const [password, setPassword] = useState('');
  const [testTo, setTestTo] = useState('');
  const [virustotalApiKey, setVirustotalApiKey] = useState('');
  const [abuseipdbApiKey, setAbuseipdbApiKey] = useState('');
  const [voiceToken, setVoiceToken] = useState('');
  const [savingNotify, setSavingNotify] = useState(false);
  const [notifyMessage, setNotifyMessage] = useState<{ type: 'ok' | 'error'; text: string } | null>(null);
  const [nvdApiKey, setNvdApiKey] = useState('');
  const [anthropicApiKey, setAnthropicApiKey] = useState('');
  const [savingAi, setSavingAi] = useState(false);
  const [testingAi, setTestingAi] = useState(false);
  const [aiMessage, setAiMessage] = useState<{ type: 'ok' | 'error'; text: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [savingThreatIntel, setSavingThreatIntel] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState<{ type: 'ok' | 'error'; text: string } | null>(null);
  const [threatIntelMessage, setThreatIntelMessage] = useState<{ type: 'ok' | 'error'; text: string } | null>(null);

  async function load() {
    const res = await axios.get('/api/settings');
    setSettings(res.data.data);
  }

  useEffect(() => { load(); }, []);

  if (user?.scopeLevel !== 'platform') {
    return (
      <div className="card p-8 text-center">
        <Lock size={24} className="mx-auto text-gray-300 mb-2" />
        <h1 className="text-lg font-semibold text-gray-900">Platform settings</h1>
        <p className="text-gray-500 text-sm">Only platform administrators can manage these settings.</p>
      </div>
    );
  }

  if (!settings) return <div className="p-4 text-gray-400">Loading…</div>;

  function update(key: keyof SmtpSettings, value: string) {
    setSettings(prev => (prev ? { ...prev, [key]: value } : prev));
  }

  async function handleSaveNotifications(e: React.FormEvent) {
    e.preventDefault();
    setSavingNotify(true);
    setNotifyMessage(null);
    try {
      await axios.put('/api/settings', {
        portal_url: settings!.portal_url,
        voice_webhook_url: settings!.voice_webhook_url,
        voice_webhook_token: voiceToken,
      });
      setVoiceToken('');
      setNotifyMessage({ type: 'ok', text: 'Notification settings saved.' });
      const res = await axios.get('/api/settings');
      setSettings(res.data.data);
    } catch (err: any) {
      setNotifyMessage({ type: 'error', text: err.response?.data?.error || 'Error while saving' });
    } finally {
      setSavingNotify(false);
    }
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      await axios.put('/api/settings', {
        smtp_host: settings!.smtp_host,
        smtp_port: settings!.smtp_port,
        smtp_secure: settings!.smtp_secure,
        smtp_user: settings!.smtp_user,
        smtp_from: settings!.smtp_from,
        smtp_pass: password,
        gateway_host: settings!.gateway_host,
        gateway_port: settings!.gateway_port,
        athena_push_allowed_ips: settings!.athena_push_allowed_ips,
      });
      setPassword('');
      await load();
      setMessage({ type: 'ok', text: 'Settings saved' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Error while saving the settings' });
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveThreatIntel(e: React.FormEvent) {
    e.preventDefault();
    setSavingThreatIntel(true);
    setThreatIntelMessage(null);
    try {
      await axios.put('/api/settings', {
        virustotal_api_key: virustotalApiKey,
        abuseipdb_api_key: abuseipdbApiKey,
        nvd_api_key: nvdApiKey,
      });
      setVirustotalApiKey(''); setAbuseipdbApiKey(''); setNvdApiKey('');
      await load();
      setThreatIntelMessage({ type: 'ok', text: 'Settings saved' });
    } catch (err: any) {
      setThreatIntelMessage({ type: 'error', text: err.response?.data?.error || 'Error while saving the settings' });
    } finally {
      setSavingThreatIntel(false);
    }
  }

  async function handleSaveAi(e: React.FormEvent) {
    e.preventDefault();
    setSavingAi(true);
    setAiMessage(null);
    try {
      await axios.put('/api/settings', { anthropic_api_key: anthropicApiKey, ai_model: settings!.ai_model });
      setAnthropicApiKey('');
      await load();
      setAiMessage({ type: 'ok', text: 'Settings saved' });
    } catch (err: any) {
      setAiMessage({ type: 'error', text: err.response?.data?.error || 'Error while saving the settings' });
    } finally {
      setSavingAi(false);
    }
  }

  async function handleTestAi() {
    setTestingAi(true);
    setAiMessage(null);
    try {
      const res = await axios.post('/api/settings/test-ai');
      setAiMessage({ type: 'ok', text: `The model ${res.data.data.model} answered in ${res.data.data.latencyMs} ms` });
    } catch (err: any) {
      setAiMessage({ type: 'error', text: err.response?.data?.error || 'AI test failed' });
    } finally {
      setTestingAi(false);
    }
  }

  async function handleTest() {
    setTesting(true);
    setMessage(null);
    try {
      await axios.post('/api/settings/test-email', { to: testTo });
      setMessage({ type: 'ok', text: `Test email sent to ${testTo}` });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Error while sending the test email' });
    } finally {
      setTesting(false);
    }
  }

  return (
    <div className="max-w-3xl">
      <h1 className="text-xl font-semibold text-gray-900 mb-1">Platform settings</h1>
      <p className="text-sm text-gray-500 mb-4">General settings applied to the whole platform, across all partners and tenants.</p>

      {message && (
        <div className={`mb-4 text-sm rounded-lg px-3 py-2 ${message.type === 'ok' ? 'text-green-700 bg-green-50' : 'text-red-700 bg-red-50'}`}>
          {message.text}
        </div>
      )}

      <SettingsCard onSubmit={handleSave} icon={<Server size={18} className="text-primary-600" />} title="Log ingestion endpoint"
        status={settings.gateway_host ? CONFIGURED : { label: 'Required', className: 'bg-amber-100 text-amber-700' }}>
        <p className="text-xs text-gray-500 mb-4">
          Public address customers' brokers connect to. It is written into every generated installer package,
          so brokers work without any manual configuration on site.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label className="block text-xs text-gray-500 mb-1">Platform host</label>
            <input value={settings.gateway_host} onChange={e => update('gateway_host', e.target.value)}
              placeholder="soc.yourdomain.com" className="input-field w-full" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Ingestion port</label>
            <input value={settings.gateway_port} onChange={e => update('gateway_port', e.target.value)}
              placeholder="6514" className="input-field w-full" />
          </div>
          <div className="md:col-span-3">
            <label className="block text-xs text-gray-500 mb-1">Athena XDR push — allowed source IPs (port 6515 TCP/JSON, 6516 TLS; comma-separated IPs or CIDRs; empty = nobody)</label>
            <input value={settings.athena_push_allowed_ips || ''} onChange={e => update('athena_push_allowed_ips', e.target.value)}
              placeholder="203.0.113.9, 198.51.100.0/24" className="input-field w-full font-mono" />
          </div>
        </div>
      </SettingsCard>

      <SettingsCard onSubmit={handleSave} icon={<Mail size={18} className="text-primary-600" />} title="Email delivery (SMTP)"
        status={settings.smtp_host ? CONFIGURED : NOT_CONFIGURED}>
        <p className="text-xs text-gray-500 mb-4">Used to send incident reports to customers. Until it is configured, reports are recorded but not actually delivered.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-xs text-gray-500 mb-1">SMTP host</label>
            <input value={settings.smtp_host} onChange={e => update('smtp_host', e.target.value)}
              placeholder="smtp.provider.com" className="input-field w-full" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Port</label>
            <input value={settings.smtp_port} onChange={e => update('smtp_port', e.target.value)}
              placeholder="587" className="input-field w-full" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Encryption</label>
            <select value={settings.smtp_secure || 'false'} onChange={e => update('smtp_secure', e.target.value)}
              className="input-field w-full">
              <option value="false">STARTTLS / none (port 587 or 25)</option>
              <option value="true">Implicit TLS (port 465)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Username</label>
            <input value={settings.smtp_user} onChange={e => update('smtp_user', e.target.value)}
              autoComplete="off" className="input-field w-full" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">
              Password {settings.smtp_pass_set && <span className="text-green-600">(saved)</span>}
            </label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)}
              placeholder={settings.smtp_pass_set ? 'Leave empty to keep the current one' : ''}
              autoComplete="new-password" className="input-field w-full" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs text-gray-500 mb-1">Sender address</label>
            <input value={settings.smtp_from} onChange={e => update('smtp_from', e.target.value)}
              placeholder="SOC Platform <soc@yourdomain.com>" className="input-field w-full" />
          </div>
        </div>

        <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100">
          <ShieldCheck size={14} className="text-gray-400" />
          <span className="text-xs text-gray-500">The password is encrypted at rest and never returned by the API.</span>
          {isAdmin && (
<button type="submit" disabled={saving} className="btn-primary ml-auto flex items-center gap-1">
            <Save size={14} /> {saving ? 'Saving…' : 'Save'}
          </button>
)}
        </div>
      </SettingsCard>

      <SettingsCard onSubmit={handleSaveThreatIntel} icon={<Radar size={18} className="text-primary-600" />} title="Threat intelligence"
        status={settings.virustotal_api_key_set || settings.abuseipdb_api_key_set ? CONFIGURED : NOT_CONFIGURED}>
        <p className="text-xs text-gray-500 mb-4">
          Used to look up the reputation of public source/destination IPs seen in alerts (VirusTotal,
          AbuseIPDB), on demand when an analyst opens an alert — not a bulk scan, so free-tier rate
          limits are not a concern under normal use. Either key can be left unset; the other source
          still works on its own.
        </p>
        {threatIntelMessage && (
          <div className={`mb-4 text-sm rounded-lg px-3 py-2 ${threatIntelMessage.type === 'ok' ? 'text-green-700 bg-green-50' : 'text-red-700 bg-red-50'}`}>
            {threatIntelMessage.text}
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">
              VirusTotal API key {settings.virustotal_api_key_set && <span className="text-green-600">(saved)</span>}
            </label>
            <input type="password" value={virustotalApiKey} onChange={e => setVirustotalApiKey(e.target.value)}
              placeholder={settings.virustotal_api_key_set ? 'Leave empty to keep the current one' : ''}
              autoComplete="new-password" className="input-field w-full font-mono text-xs" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">
              AbuseIPDB API key {settings.abuseipdb_api_key_set && <span className="text-green-600">(saved)</span>}
            </label>
            <input type="password" value={abuseipdbApiKey} onChange={e => setAbuseipdbApiKey(e.target.value)}
              placeholder={settings.abuseipdb_api_key_set ? 'Leave empty to keep the current one' : ''}
              autoComplete="new-password" className="input-field w-full font-mono text-xs" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs text-gray-500 mb-1">
              NVD API key (optional) {settings.nvd_api_key_set && <span className="text-green-600">(saved)</span>}
            </label>
            <input type="password" value={nvdApiKey} onChange={e => setNvdApiKey(e.target.value)}
              placeholder={settings.nvd_api_key_set ? 'Leave empty to keep the current one' : ''}
              autoComplete="new-password" className="input-field w-full font-mono text-xs" />
            <p className="text-xs text-gray-400 mt-1">
              Used by the installed-software vulnerability check (Vulnerabilities page) to download CVE data from
              NVD. It works without a key, just more slowly: NVD allows 5 requests every 30 seconds without one and
              50 with one. Free key at nvd.nist.gov/developers/request-an-api-key.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100">
          <ShieldCheck size={14} className="text-gray-400" />
          <span className="text-xs text-gray-500">Keys are encrypted at rest and never returned by the API.</span>
          {isAdmin && (
<button type="submit" disabled={savingThreatIntel} className="btn-primary ml-auto flex items-center gap-1">
            <Save size={14} /> {savingThreatIntel ? 'Saving…' : 'Save'}
          </button>
)}
        </div>
      </SettingsCard>

      <SettingsCard onSubmit={handleSaveAi} icon={<Sparkles size={18} className="text-primary-600" />} title="AI report insights"
        status={settings.anthropic_api_key_set ? CONFIGURED : NOT_CONFIGURED}>
        <p className="text-xs text-gray-500 mb-4">
          Used by the "AI security considerations" page of the customer reports (enable it per template in Report
          Templates): a Claude model reads the period's aggregated facts — incidents, open vulnerabilities, log source
          and broker availability, critical assets, noisiest rules — and writes an assessment with prioritised actions.
          Only aggregated numbers and names are sent, never raw logs. Without a key the page says the section is not
          available and the rest of the report is generated normally.
        </p>
        {aiMessage && (
          <div className={`mb-4 text-sm rounded-lg px-3 py-2 ${aiMessage.type === 'ok' ? 'text-green-700 bg-green-50' : 'text-red-700 bg-red-50'}`}>
            {aiMessage.text}
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">
              Anthropic API key {settings.anthropic_api_key_set && <span className="text-green-600">(saved)</span>}
            </label>
            <input type="password" value={anthropicApiKey} onChange={e => setAnthropicApiKey(e.target.value)}
              placeholder={settings.anthropic_api_key_set ? 'Leave empty to keep the current one' : 'sk-ant-…'}
              autoComplete="new-password" className="input-field w-full font-mono text-xs" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Model (empty = claude-opus-5-5)</label>
            <input value={settings.ai_model} onChange={e => update('ai_model', e.target.value)}
              placeholder="claude-opus-5-5" className="input-field w-full font-mono text-xs" />
          </div>
        </div>
        <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100">
          <ShieldCheck size={14} className="text-gray-400" />
          <span className="text-xs text-gray-500">The key is encrypted at rest and never returned by the API.</span>
          {isAdmin && (
            <div className="ml-auto flex items-center gap-2">
              <button type="button" onClick={handleTestAi} disabled={testingAi || !settings.anthropic_api_key_set} className="btn-secondary flex items-center gap-1 disabled:opacity-50">
                <Send size={14} /> {testingAi ? 'Testing…' : 'Test'}
              </button>
              <button type="submit" disabled={savingAi} className="btn-primary flex items-center gap-1">
                <Save size={14} /> {savingAi ? 'Saving…' : 'Save'}
              </button>
            </div>
          )}
        </div>
      </SettingsCard>

      <SettingsCard onSubmit={handleSaveNotifications} icon={<Mail size={18} className="text-primary-600" />} title="Alert notifications"
        status={settings.portal_url || settings.voice_webhook_url ? CONFIGURED : NOT_CONFIGURED}>
        <p className="text-xs text-gray-500 mb-4">
          Alerts are emailed (SMTP above) and, when a tenant's notification rules ask for a phone call, forwarded to a
          voice webhook: a JSON POST per call with the number and a ready-to-speak text, for a Twilio/Vonage/PBX
          relay. Without a webhook, phone calls appear in the notification bell as "Phone call required" for the
          operator on duty. Recipients and day/night preferences are set per tenant in the Onboarding wizard.
        </p>
        {notifyMessage && (
          <div className={`mb-4 text-sm rounded-lg px-3 py-2 ${notifyMessage.type === 'ok' ? 'text-green-700 bg-green-50' : 'text-red-700 bg-red-50'}`}>
            {notifyMessage.text}
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Portal URL (link in notification emails)</label>
            <input value={settings.portal_url} onChange={e => update('portal_url', e.target.value)}
              placeholder="https://soc.example.com" className="input-field w-full" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Voice call webhook URL (optional)</label>
            <input value={settings.voice_webhook_url} onChange={e => update('voice_webhook_url', e.target.value)}
              placeholder="https://relay.example.com/soc-call" className="input-field w-full" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs text-gray-500 mb-1">
              Webhook bearer token (optional) {settings.voice_webhook_token_set && <span className="text-green-600">(saved)</span>}
            </label>
            <input type="password" value={voiceToken} onChange={e => setVoiceToken(e.target.value)}
              placeholder={settings.voice_webhook_token_set ? 'Leave empty to keep the current one' : ''}
              autoComplete="new-password" className="input-field w-full font-mono text-xs" />
          </div>
        </div>
        <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100">
          <ShieldCheck size={14} className="text-gray-400" />
          <span className="text-xs text-gray-500">The token is encrypted at rest and never returned by the API.</span>
          {isAdmin && (
            <button type="submit" disabled={savingNotify} className="btn-primary ml-auto flex items-center gap-1">
              <Save size={14} /> {savingNotify ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
      </SettingsCard>

      <SettingsCard icon={<Send size={18} className="text-primary-600" />} title="Send a test email">
        <div className="flex gap-2">
          <input type="email" value={testTo} onChange={e => setTestTo(e.target.value)}
            placeholder="your.address@company.com" className="input-field flex-1" />
          <button onClick={handleTest} disabled={testing || !testTo} className="btn-secondary flex items-center gap-1">
            <Send size={14} /> {testing ? 'Sending…' : 'Send test'}
          </button>
        </div>
      </SettingsCard>
    </div>
  );
}
